/**
 * Trovva — Role-Based Access Control (RBAC)
 *
 * Defines what each role can see and do across the platform.
 * This is the single source of truth for permissions.
 *
 * Roles:
 *   admin    — Full platform access (Govind, tech admin)
 *   broker   — Full brokerage access (Jenifer — Broker of Record)
 *   agent    — Own leads, own deals, AI tools, own commissions
 *   pm_manager — Property management, maintenance, tenants/owners
 *   viewer   — Read-only access to assigned modules
 *
 * Future: When productizing for independent agents, an agent with
 * no org becomes their own "broker" — same permissions, solo context.
 */

// ============================================================
// MODULE DEFINITIONS
// ============================================================

export const MODULES = {
  dashboard: { label: 'Dashboard', description: 'Overview and alerts' },
  leads: { label: 'CRM', description: 'Lead pipeline and contacts' },
  activity: { label: 'Agent Activity', description: 'Task tracking and agent performance' },
  transactions: { label: 'Transactions', description: 'Deal tracking and milestones' },
  listings: { label: 'Listing Hub', description: 'Create listings once, copy to every MLS' },
  financials: { label: 'Financials', description: 'Revenue, commissions, reconciliation' },
  reports: { label: 'Reports', description: 'Analytics, pipeline, agent performance, lead source ROI' },
  // showings: KILLED — folded into CRM lead detail as "Schedule Showing" action
  // --- Property Management ---
  pm: { label: 'Properties & Leases', description: 'Leases, renewals, tenant & property portfolio' },
  maintenance: { label: 'Maintenance', description: 'Work orders and vendor dispatch' },
  // --- Tools & Admin ---
  ai_tools: { label: 'AI Studio', description: 'AI that drafts for you — by audience, not output type (accessed via Listing Hub)' },
  calendar: { label: 'Calendar', description: 'Your Google Calendar — manage events by text or voice via Reminderly' },
  my_day: { label: 'My Day', description: "Today's tasks, overdue sweep, and upcoming work — finish from web or WhatsApp" },
  integrations: { label: 'Integrations', description: 'Connect Gmail, Calendar, and other services' },
  settings: { label: 'Team & HR', description: 'Team management and onboarding' },
  // commissions merged into financials — agents see My Earnings, brokers see full Financials
};

// ============================================================
// ROLE → PERMISSIONS MAP
// ============================================================

export const ROLE_PERMISSIONS = {
  admin: {
    modules: ['dashboard', '---brokerage', 'leads', 'activity', 'transactions', '---pm', 'pm', 'maintenance', '---tools', 'listings', 'financials', 'reports', 'recruiting', 'settings_full'],
    dataScope: 'all',          // Sees all data across org
    canManageTeam: true,
    canViewAllLeads: true,
    canViewAllDeals: true,
    canViewBrokerageFinancials: true,
    canViewPM: true,
    canExport: true,
    canDelete: true,
    dashboardType: 'broker',   // Full broker dashboard
  },

  broker: {
    modules: ['dashboard', '---brokerage', 'leads', 'activity', 'transactions', '---pm', 'pm', 'maintenance', '---tools', 'listings', 'financials', 'reports', 'settings_full'],
    dataScope: 'all',
    canManageTeam: true,
    canViewAllLeads: true,
    canViewAllDeals: true,
    canViewBrokerageFinancials: true,
    canViewPM: true,
    canExport: true,
    canDelete: true,
    dashboardType: 'broker',
  },

  agent: {
    modules: ['dashboard', 'my_day', 'leads', 'transactions', 'listings', 'financials', 'settings_full'],
    dataScope: 'own',          // Only sees their own data
    canManageTeam: false,
    canViewAllLeads: false,    // Only their assigned leads
    canViewAllDeals: false,    // Only their deals
    canViewBrokerageFinancials: false,
    canViewPM: false,
    canExport: false,
    canDelete: false,
    dashboardType: 'agent',    // Agent-specific dashboard
  },

  pm_manager: {
    modules: ['dashboard', 'maintenance', 'pm', 'leads'],
    dataScope: 'pm',           // Only PM-related data
    canManageTeam: false,
    canViewAllLeads: false,    // Only renter/PM owner leads
    canViewAllDeals: false,
    canViewBrokerageFinancials: false,
    canViewPM: true,
    canExport: false,
    canDelete: false,
    dashboardType: 'pm',       // PM-focused dashboard
  },

  viewer: {
    modules: ['dashboard'],
    dataScope: 'own',
    canManageTeam: false,
    canViewAllLeads: false,
    canViewAllDeals: false,
    canViewBrokerageFinancials: false,
    canViewPM: false,
    canExport: false,
    canDelete: false,
    dashboardType: 'viewer',
  },
};

// ============================================================
// NAV ITEMS PER ROLE
// Each entry maps to a sidebar/mobile nav item
// ============================================================

export const NAV_CONFIG = {
  dashboard:    { icon: 'Home',       label: 'Dashboard',    mobileLabel: 'Home' },
  leads:        { icon: 'Users',      label: 'CRM',  mobileLabel: 'Leads' },
  activity:     { icon: 'Target',     label: 'Agent Activity', mobileLabel: 'Activity' },
  maintenance:  { icon: 'Wrench',     label: 'Maintenance',   mobileLabel: 'Maint' },
  transactions: { icon: 'FileText',   label: 'Transactions',  mobileLabel: 'Deals' },
  listings:     { icon: 'ClipboardList', label: 'Listing Hub', mobileLabel: 'Hub' },
  financials:   { icon: 'DollarSign', label: 'Financials',    mobileLabel: 'Money' },
  // ai_tools merged into listings — Studio is now a tab inside Listing Hub
  reports:      { icon: 'BarChart3',   label: 'Reports',       mobileLabel: 'Reports' },
  // showings removed — folded into CRM lead detail
  calendar:     { icon: 'Calendar',   label: 'Calendar',      mobileLabel: 'Cal' },
  my_day:       { icon: 'Sun',        label: 'My Day',        mobileLabel: 'Day' },
  pm:           { icon: 'Building',   label: 'Properties & Leases', mobileLabel: 'PM' },
  // commissions merged into financials
  recruiting:   { icon: 'UserPlus',   label: 'Recruiting',    mobileLabel: 'Recruit' },
  settings_full: { icon: 'Settings', label: 'Settings',      mobileLabel: 'Settings' },
};

// ============================================================
// HELPER FUNCTIONS
// ============================================================

/**
 * Get permissions for a role
 */
export function getPermissions(role) {
  return ROLE_PERMISSIONS[role] || ROLE_PERMISSIONS.viewer;
}

/**
 * Check if a role has access to a specific module
 */
export function canAccess(role, moduleId) {
  const perms = getPermissions(role);
  return perms.modules.includes(moduleId);
}

/**
 * Get the nav items for a role (ordered)
 */
// Section divider labels shown in sidebar
const SECTION_LABELS = {
  '---brokerage': 'Brokerage',
  '---pm': 'Property Management',
  '---tools': 'Tools & Admin',
};

export function getNavItems(role) {
  const perms = getPermissions(role);
  return perms.modules.map(id => {
    // Section dividers start with '---'
    if (id.startsWith('---')) {
      return { id, type: 'divider', label: SECTION_LABELS[id] || '' };
    }
    return { id, ...NAV_CONFIG[id] };
  }).filter(item => item.type === 'divider' || item.icon); // Keep dividers + valid items
}

/**
 * Get the user's display role label
 */
export function getRoleLabel(role) {
  const labels = {
    admin: 'Admin',
    broker: 'Broker of Record',
    agent: 'Agent',
    pm_manager: 'Property Manager',
    viewer: 'Viewer',
  };
  return labels[role] || 'Team Member';
}

/**
 * Extract the effective role from a Supabase user object.
 * Checks user_metadata first, falls back to 'agent'.
 */
export function getUserRole(user) {
  if (!user) return 'viewer';
  // Check user_metadata (set during createUser)
  const metaRole = user.user_metadata?.role;
  if (metaRole && ROLE_PERMISSIONS[metaRole]) return metaRole;
  // Default
  return 'agent';
}

/**
 * Filter leads based on role and user ID
 */
export function filterDataByRole(data, role, userId, field = 'assigned_to') {
  const perms = getPermissions(role);
  if (perms.dataScope === 'all') return data;
  // For 'own' scope, filter to user's data
  return data.filter(item => item[field] === userId);
}

// ============================================================
// SIMPLE MODE — Feature Toggle System
// ============================================================

/**
 * Advanced features that are hidden when Simple Mode is ON.
 * Each key maps to a user-facing feature the broker can toggle.
 */
export const ADVANCED_FEATURES = [
  'automations',           // Automation rules engine
  'smart_lists',           // Smart list builder (Segments)
  'lead_scoring',          // AI lead scoring
  'action_plans',          // Drip campaign action plans
  'zapier',                // Zapier integration
  'lead_capture_forms',    // Embeddable lead forms
  'sms_campaigns',         // Bulk SMS campaigns
  'email_campaigns',       // Bulk email campaigns
  'duplicate_detection',   // Contact dedup
  'deal_room',             // Client portal
];

/**
 * Check if a feature is visible to the user.
 * In Simple Mode, advanced features are hidden.
 * In Full Mode, all features are visible.
 *
 * @param {string} featureKey - The feature identifier (e.g., 'automations')
 * @param {boolean} simpleMode - Whether Simple Mode is enabled
 * @returns {boolean} True if the feature should be visible
 */
export function isFeatureVisible(featureKey, simpleMode) {
  if (!simpleMode) return true; // Everything visible in full mode
  return !ADVANCED_FEATURES.includes(featureKey);
}

/**
 * Get nav items for a role, optionally filtering by Simple Mode.
 * Simple Mode hides tabs that map to advanced features.
 *
 * @param {string} role - The user's role
 * @param {boolean} simpleMode - Whether Simple Mode is enabled (optional)
 * @returns {Array} Array of nav items, filtered if simpleMode is true
 */
export function getSimpleModeNavItems(role, simpleMode = false) {
  const baseItems = getNavItems(role);

  // If not in simple mode, return all items as-is
  if (!simpleMode) return baseItems;

  // In simple mode, filter out nav items that map to advanced features
  // Currently, no top-level nav items are hidden in simple mode.
  // Advanced features are primarily hidden as sub-tabs within the CRM view.
  return baseItems;
}
